import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  CardDetailBundleModel,
  CardDetailsModel,
  CardOwnerOptionModel,
  CardStageOptionModel,
  UpdateCardCompletionRequestModel,
  UpdateCardDueDateRequestModel,
  UpdateCardOwnerRequestModel,
  UpdateCardRequestModel,
} from '../models/card-details-api.models';
import { asArray, asBoolean, asNullableString, asNumber, asRecord, asString } from './card-api.utils';

@Injectable({ providedIn: 'root' })
export class CardDetailsApiService {
  private readonly http = inject(HttpClient);

  async getCardById(cardId: string): Promise<CardDetailsModel | null> {
    const response = await firstValueFrom(this.http.get<unknown>(`/api/cards/${cardId}`));
    return this.parseCardDetailsModel(asRecord(response));
  }

  /**
   * GET /api/cards/{id}/detail — um pedido com card, workflow, projeto, stages, donos, workflowFields e fieldValues.
   * Se a rota não existir (404), devolve `null` para o cliente fazer fallback ao fluxo antigo.
   */
  async tryGetCardDetailBundle(cardId: string): Promise<CardDetailBundleModel | null> {
    const encoded = encodeURIComponent(cardId);
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(`/api/cards/${encoded}/detail`)
      );
      const payload = this.unwrapCardDetailEnvelope(response);
      const cardSource = asRecord(payload['card'] ?? payload['Card']);
      const card = this.parseCardDetailsModel(cardSource);
      if (!card?.id) return null;

      const workflow = asRecord(payload['workflow'] ?? payload['Workflow']);
      const project = asRecord(payload['project'] ?? payload['Project']);
      const workflowId = asString(workflow['id']) || card.workflowId;
      const projectIdFromWorkflow = asString(workflow['projectId'] ?? workflow['ProjectId']);
      const projectId = projectIdFromWorkflow || asString(project['id']);
      const workflowName = asString(workflow['name']) || '-';
      const projectName = asString(project['name']) || '-';

      const stagesRaw = asArray(payload['stages'] ?? payload['Stages']);
      const stageOptions: CardStageOptionModel[] = stagesRaw
        .map((row) => {
          const r = asRecord(row);
          return {
            id: asString(r['id']),
            name: asString(r['name']) || asString(r['id']),
            order: asNumber(r['order'], 0),
          };
        })
        .filter((row) => !!row.id)
        .sort((a, b) => a.order - b.order)
        .map(({ id, name }) => ({ id, name }));

      const ownersRaw = asArray(payload['ownerOptions'] ?? payload['OwnerOptions']);
      const ownerOptions: CardOwnerOptionModel[] = ownersRaw
        .map((row) => asRecord(row))
        .map((row) => ({
          id: asString(row['id']),
          name: asString(row['name']) || asString(row['fullName']) || asString(row['email']),
          email: asNullableString(row['email'] ?? row['Email']) ?? undefined,
          avatarUrl: this.pickNullableString(
            row['avatarUrl'],
            row['AvatarUrl'],
            row['photoUrl'],
            row['PhotoUrl']
          ),
        }))
        .filter((o) => !!o.id && !!o.name);

      return {
        card,
        projectId,
        projectName,
        workflowName,
        stageOptions,
        ownerOptions,
        workflowFieldsRaw: payload['workflowFields'] ?? payload['WorkflowFields'] ?? [],
        fieldValuesRaw: payload['fieldValues'] ?? payload['FieldValues'] ?? [],
      };
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        return null;
      }
      throw err;
    }
  }

  private unwrapCardDetailEnvelope(raw: unknown): Record<string, unknown> {
    const root = asRecord(raw);
    const inner = asRecord(root['data'] ?? root['Data']);
    if (Object.keys(inner).length && (inner['card'] ?? inner['Card'])) {
      return inner;
    }
    return root;
  }

  private parseCardDetailsModel(source: Record<string, unknown>): CardDetailsModel | null {
    const id = asString(source['id']);
    if (!id) return null;

    return {
      id,
      workflowId: asString(source['workflowId']),
      workflowStageId: asString(source['workflowStageId']),
      title: asString(source['title']),
      description: this.pickNullableString(
        source['description'],
        source['Description'],
        source['cardDescription'],
        source['CardDescription'],
        source['descricao'],
        source['Descricao'],
        source['details'],
        source['Details']
      ),
      code: asNullableString(source['code']),
      position: typeof source['position'] === 'number' ? source['position'] : null,
      isCompleted: asBoolean(source['isCompleted']),
      dueDate: asNullableString(source['dueDate']),
      ownerUserId: asNullableString(source['ownerUserId']),
      createdByUserId: this.pickCreatedByUserId(source),
      createdAt: this.pickNullableString(
        source['createdAt'],
        source['createdOn'],
        source['createdDate'],
        source['creationDate'],
        source['insertedAt']
      ),
      updatedAt: this.pickNullableString(
        source['updatedAt'],
        source['updatedOn'],
        source['updatedDate'],
        source['lastUpdatedAt'],
        source['modifiedAt']
      ),
    };
  }

  async updateCard(cardId: string, payload: UpdateCardRequestModel): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}`, payload));
  }

  async updateOwner(cardId: string, payload: UpdateCardOwnerRequestModel): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}/owner`, payload));
  }

  async updateDueDate(cardId: string, payload: UpdateCardDueDateRequestModel): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}/duedate`, payload));
  }

  async updateCompletion(cardId: string, payload: UpdateCardCompletionRequestModel): Promise<void> {
    await firstValueFrom(this.http.put(`/api/cards/${cardId}/completion`, payload));
  }

  /**
   * MoveCardRequest: cardId, targetWorkflowStageId, targetPosition (≥ 1).
   * Para “colar no fim” sem contar irmãos, usar valor alto — a API faz clamp.
   */
  async moveCard(
    cardId: string,
    payload: { targetWorkflowStageId: string; targetPosition: number }
  ): Promise<void> {
    await firstValueFrom(
      this.http.put(`/api/cards/${encodeURIComponent(cardId)}/move`, {
        cardId,
        targetWorkflowStageId: payload.targetWorkflowStageId,
        targetPosition: Math.trunc(payload.targetPosition),
      })
    );
  }

  async deleteCard(cardId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`/api/cards/${cardId}`));
  }

  async getCardStageOptions(workflowId: string): Promise<CardStageOptionModel[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(`/api/workflows/${workflowId}/stages`)
    );
    return asArray(response)
      .map((item) => asRecord(item))
      .map((item) => ({
        id: asString(item['id']),
        name: asString(item['name']) || asString(item['id']),
        order: asNumber(item['order'], 0),
      }))
      .filter((item) => !!item.id)
      .sort((a, b) => a.order - b.order)
      .map((item) => ({ id: item.id, name: item.name }));
  }

  async getOwnerOptions(): Promise<CardOwnerOptionModel[]> {
    const response = await firstValueFrom(this.http.get<unknown>('/users'));
    return asArray(response)
      .map((item) => asRecord(item))
      .map((item) => ({
        id: asString(item['id']),
        name: asString(item['name']) || asString(item['fullName']) || asString(item['email']),
        avatarUrl: this.pickNullableString(
          item['avatarUrl'],
          item['AvatarUrl'],
          item['photoUrl'],
          item['PhotoUrl'],
          item['imageUrl'],
          item['ImageUrl'],
          item['profilePictureUrl'],
          item['profileImageUrl'],
          item['ProfileImageUrl']
        ),
      }))
      .filter((item) => !!item.id && !!item.name);
  }

  /**
   * GET /api/cards/:id — contrato: `createdByUserId` (GUID string, camelCase), alinhado a `ownerUserId` e /users.
   * Null quando o card não tem criador autenticado (ex.: formulário público). Fallbacks extra por compat.
   */
  private pickCreatedByUserId(source: Record<string, unknown>): string | null {
    const primary = asNullableString(source['createdByUserId']);
    if (primary?.trim()) return primary.trim();
    const legacy =
      asNullableString(source['createdById']) ||
      asNullableString(source['authorUserId']) ||
      asNullableString(source['createdByUser']);
    if (legacy?.trim()) return legacy.trim();
    const nested = asRecord(source['createdBy']);
    const fromNested =
      asNullableString(nested['id']) ||
      asNullableString(nested['userId']) ||
      asNullableString(nested['userID']);
    return fromNested?.trim() || null;
  }

  private pickNullableString(...values: unknown[]): string | null {
    for (const value of values) {
      const parsed = asNullableString(value);
      if (parsed && parsed.trim().length > 0) {
        return parsed;
      }
    }
    return null;
  }
}
